/**
 * Start of the guided listing setup (US-109): pick the Unit by its display name. A Unit that
 * already has a non-closed listing is marked "Resume setup" (a draft) or "Already listed".
 * Route: /leasing/listings/new
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { fetchListings, fetchUnits, type Listing, type UnitListItem } from '../../../lib/leasing.api';
import { formatUnitLabel } from '@resida/shared';

function unitLabel(unit: UnitListItem): string {
  return [formatUnitLabel(unit.unitNumber), unit.propertyName].filter(Boolean).join(' · ');
}

const LISTINGS_PAGE_SIZE = 100; // the API's maximum

/**
 * Every non-closed listing, across all pages: a unit whose listing falls beyond the first page must
 * still read "Resume setup" / "Already listed", never "Start setup" (a second create is a 409).
 */
async function fetchAllNonClosedListings(): Promise<Listing[]> {
  const items: Listing[] = [];
  for (let page = 1; ; page += 1) {
    const result = await fetchListings({ status: ['draft', 'active', 'paused'], page, pageSize: LISTINGS_PAGE_SIZE });
    items.push(...result.items);
    if (result.items.length === 0 || items.length >= result.totalCount) return items;
  }
}

export function ListingGuideStartPage() {
  const [unitId, setUnitId] = useState('');
  const {
    data: unitsData,
    isLoading: unitsLoading,
    error: unitsError,
    refetch: refetchUnits,
  } = useQuery({ queryKey: ['units'], queryFn: () => fetchUnits() });
  const { data: listingsData } = useQuery({
    queryKey: ['listings', 'guide-start'],
    queryFn: fetchAllNonClosedListings,
  });

  const units = unitsData?.units ?? [];
  const listingByUnit = new Map<string, Listing>((listingsData ?? []).map((l) => [l.unitId, l]));
  const existing = unitId ? listingByUnit.get(unitId) : undefined;

  return (
    <div data-testid="listing-guide-start" className="mx-auto w-full max-w-xl px-[var(--page-padding-x)] pb-6">
      <Link
        to="/leasing/listings"
        data-testid="guide-start-back"
        className="mb-2 inline-flex min-h-[44px] items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        All Listings
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">New listing</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Pick the unit you want to list. Seven short steps take it from here to live, and each is saved as you go.
      </p>

      <div className="mt-6 rounded-xl border border-border bg-card p-[var(--card-padding)]">
        <label htmlFor="guide-start-unit" className="mb-1 block text-sm font-medium text-foreground">
          Unit
        </label>
        {unitsLoading && <Skeleton className="h-11 w-full" data-testid="create-listing-unit-loading" />}
        {unitsError && (
          <div role="alert" data-testid="create-listing-unit-error" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <p className="text-destructive">Failed to load units.</p>
            <Button type="button" variant="outline" className="mt-2" data-testid="create-listing-unit-retry" onClick={() => void refetchUnits()}>
              Retry
            </Button>
          </div>
        )}
        {!unitsLoading && !unitsError && units.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="create-listing-unit-empty">
            You don&apos;t have any units yet.{' '}
            <Link
              to="/units"
              data-testid="create-listing-unit-empty-link"
              className="inline-flex min-h-[44px] items-center text-foreground underline"
            >
              Add one first
            </Link>
            .
          </p>
        )}
        {!unitsLoading && !unitsError && units.length > 0 && (
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger id="guide-start-unit" data-testid="create-listing-unit-picker" aria-label="Select unit">
              <SelectValue placeholder="Select a unit…" />
            </SelectTrigger>
            <SelectContent>
              {units.map((unit) => {
                const listing = listingByUnit.get(unit.id);
                return (
                  <SelectItem key={unit.id} value={unit.id} data-testid={`unit-option-${unit.id}`}>
                    {unitLabel(unit)}
                    {listing ? (listing.status === 'draft' ? ' (draft in progress)' : ' (already listed)') : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}

        {unitId && existing?.status === 'draft' && (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="guide-start-draft-note">
            This unit has a draft listing. Pick up where you left off.
          </p>
        )}
        {unitId && existing && existing.status !== 'draft' && (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="guide-start-listed-note">
            This unit already has a listing.
          </p>
        )}

        <div className="mt-4 flex justify-end">
          {!unitId && (
            <Button type="button" disabled data-testid="guide-start-continue">
              Start setup
            </Button>
          )}
          {unitId && !existing && (
            <Button asChild>
              <Link to={`/leasing/listings/new/${unitId}`} data-testid="guide-start-continue">
                Start setup
              </Link>
            </Button>
          )}
          {unitId && existing?.status === 'draft' && (
            <Button asChild>
              <Link to={`/leasing/listings/${existing.id}`} data-testid="guide-start-resume">
                Resume setup
              </Link>
            </Button>
          )}
          {unitId && existing && existing.status !== 'draft' && (
            <Button asChild variant="outline">
              <Link to={`/leasing/listings/${existing.id}`} data-testid="guide-start-open-listing">
                Already listed
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
